import React from 'react';
import { useNavigate } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { Points } from 'components/Points/Points';
import { formatRelativeTime } from 'utils/relativeTime';
import { buildCardUrl } from 'utils/cardUrlUtils';
import type { FeedTrade, FeedTradeItem, FeedTradeStatus } from 'types/interfaces/feed';
import FeedCardImage from './FeedCardImage';
import './FeedPackageBlock.css';

const STATUS_LABEL: Record<FeedTradeStatus, string> = {
  ready: 'Ready to Ship',
  transit: 'In Transit',
  completed: 'Completed',
};

const STATUS_COLOR: Record<FeedTradeStatus, string> = {
  ready: '#3b82f6',
  transit: '#f59e0b',
  completed: '#10b981',
};

interface FeedPackageBlockProps {
  trade: FeedTrade;
}

export const FeedPackageBlock: React.FC<FeedPackageBlockProps> = ({ trade }) => {
  const navigate = useNavigate();

  const goToProfile = (username: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/profile/u/${username}`);
  };

  const goToCard = (item: FeedTradeItem) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(
      buildCardUrl({
        name: item.name,
        setCode: item.setCode,
        scryfallId: item.scryfallId,
      }),
    );
  };

  return (
    <div className="feed-package-block">
      <div className="feed-package-block__header">
        <button
          type="button"
          className="feed-package-block__user"
          onClick={goToProfile(trade.senderUsername)}
        >
          <AvatarWithFlag
            size={32}
            avatarUrl={trade.senderAvatar ?? undefined}
            username={trade.senderUsername}
            countryCode={trade.senderCountryCode ?? undefined}
          />
          <span className="feed-package-block__username">{trade.senderUsername}</span>
        </button>

        <span className="feed-package-block__arrow" aria-hidden="true">→</span>

        <button
          type="button"
          className="feed-package-block__user"
          onClick={goToProfile(trade.receiverUsername)}
        >
          <AvatarWithFlag
            size={32}
            avatarUrl={trade.receiverAvatar ?? undefined}
            username={trade.receiverUsername}
            countryCode={trade.receiverCountryCode ?? undefined}
          />
          <span className="feed-package-block__username">{trade.receiverUsername}</span>
        </button>

        <div className="feed-package-block__meta">
          <Points points={trade.totalValue} type="small" />
          <span
            className="feed-package-block__status"
            style={{ backgroundColor: STATUS_COLOR[trade.status] }}
          >
            {STATUS_LABEL[trade.status]}
          </span>
          <span
            className="feed-package-block__timestamp"
            title={new Date(trade.initiatedAt).toLocaleString()}
          >
            {formatRelativeTime(trade.initiatedAt)}
          </span>
          <span className="feed-package-block__trade-number">#{trade.tradeNumber}</span>
        </div>
      </div>

      <div className="feed-package-block__strip">
        {trade.items.map((item) => (
          <div key={item.id} className="feed-package-block__card-wrap">
            <FeedCardImage
              scryfallId={item.scryfallId}
              name={item.name}
              onClick={goToCard(item)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeedPackageBlock;
